/**
 * Acquisition (M&A) + multi-depot slice.
 *
 * Owned depots structure:
 *   {
 *     id, name, brand: 'hst'|'transnord'|'logistar'|'ecoroute'|'independant',
 *     openedDay, capacityVehicles, isHeadquarters
 *   }
 *
 * Pending acquisition structure (one at a time):
 *   {
 *     competitorId, stage, stageStartDay, daysInStage,
 *     analysis, askingPrice, weaknessLeverage,
 *     negotiationOutcome,  // { offerPrice, isAccepted, strategy, discount }
 *     antitrustOutcome,    // { isAccepted, reason }
 *   }
 */
import { COMPETITORS } from '../../data/competitors';
import { ACQUISITION_STAGES, getStageById } from '../../data/acquisitionStages';
import { analyzeCompetitor } from '../../utils/competitorAnalysis';
import {
  computeAskingPrice,
  evaluateNegotiation,
  simulateDriverRetention,
  generateCompetitorDrivers,
  generateCompetitorVehicles,
  generateInheritedContracts,
  seededRng,
} from '../../utils/acquisition';
import { MA_INTEGRATION_ONE_SHOT_COST, MAX_DEPOTS, MAX_ACQUIRED_DEPOTS, MAX_OPENED_DEPOTS, splitDepots, getMaxDepotsForPhase, DEFAULT_DEPOT_CAPACITY } from '../../data/depotEconomics';
import { RELATION_DELTAS } from '../../utils/competitorRelations';
import { getFinanceSender } from '../helpers/financeSender';
import { proRatedHolidayAllowance } from '../helpers/driverHolidayTick';
import { findCityById } from '../../data/mapCities';
import {
  COMPETITOR_HOME_CITIES,
  OPEN_DEPOT_SUBSIDY_RATE,
  MAX_INTERNATIONAL_DEPOTS,
  getDepotCityId,
} from '../../data/depotGeo';
import { seedDepotInvestmentsForFleet, depotPhaseFromInvestments } from '../../data/depotPhases';
import { tryPromotePhase } from '../helpers/phaseProgression';

// 25/07/2026 — Retour user prod « saturé 5 places » : la capacité de base
// est désormais partagée avec depotEconomics (10 places).
const NEW_DEPOT_OPEN_COST = 35000;      // € pour ouvrir un dépôt indépendant

export const createAcquisitionSlice = (set, get) => ({
  // ============== STATE ==============
  // Cohort of owned depots. Headquarters depot is always present at game start.
  ownedDepots: [],
  // Currently consulted depot id (UI tab state).
  activeDepotId: null,
  // The active acquisition, if any.
  pendingAcquisition: null,
  // List of competitor ids already acquired (no double-buy).
  acquiredCompetitorIds: [],
  // Last completed acquisition summary (for the closing modal).
  lastAcquisitionSummary: null,
  // Day at which the last LOI was refused (per competitor) — cooldown 90 days.
  loiRefusalCooldown: {}, // { [competitorId]: day }

  // ============== INIT ==============
  /**
   * Called from createCompany() so the player starts with a single HQ depot.
   */
  bootstrapDepots: (companyCity = 'lyon') => {
    const city = findCityById(companyCity);
    const hq = {
      id: 'depot-hq',
      name: `Dépôt ${city?.name || companyCity} (siège)`,
      brand: 'hst',
      openedDay: 0,
      capacityVehicles: DEFAULT_DEPOT_CAPACITY,
      isHeadquarters: true,
      // 13/06/2026 — Chantier Dépôts : ancrage géographique du QG.
      cityId: city ? city.id : 'lyon',
    };
    set({
      ownedDepots: [hq],
      activeDepotId: hq.id,
      pendingAcquisition: null,
      acquiredCompetitorIds: [],
      lastAcquisitionSummary: null,
      loiRefusalCooldown: {},
    });
  },

  // ============== ANALYSIS ==============
  getCompetitorAnalyses: () => {
    const state = get();
    // Lazy import to avoid circular ref.
    const { SUPPORT_ROLES } = require('../../data/supportRoles');
    return COMPETITORS
      .filter(c => !state.acquiredCompetitorIds.includes(c.id))
      .map(c => ({
        competitor: c,
        analysis: analyzeCompetitor(c, {
          hiredSupport: state.hiredSupport || {},
          supportRoles: SUPPORT_ROLES,
          totalDays: state.totalDays || 0,
          playerState: state,
        }),
      }));
  },

  // ============== ACQUISITION FLOW ==============
  /**
   * Start the acquisition wizard for `competitorId`.
   * Pre-conditions:
   *   - the 4 support families must be hired (analysis unlocked)
   *   - no acquisition currently pending
   *   - the competitor hasn't been acquired yet
   *   - max-depot quota not reached
   *   - if a previous LOI was refused, cooldown elapsed
   */
  startAcquisition: (competitorId) => {
    const state = get();
    if (state.pendingAcquisition) return { ok: false, reason: 'acquisition_in_progress' };
    if (state.acquiredCompetitorIds.includes(competitorId)) return { ok: false, reason: 'already_acquired' };
    // 28/05/2026 v2 : the depot cap is split into 3 opened + 3 acquired.
    // Acquisitions go to the acquired bucket — refuse only when THAT cap
    // is reached, not the total cap.
    const { acquired } = splitDepots(state.ownedDepots || []);
    if (acquired.length >= MAX_ACQUIRED_DEPOTS) return { ok: false, reason: 'max_acquired_depots_reached' };
    const cooldownDay = state.loiRefusalCooldown[competitorId];
    if (cooldownDay && (state.totalDays - cooldownDay) < 90) {
      return { ok: false, reason: 'loi_cooldown', daysLeft: 90 - (state.totalDays - cooldownDay) };
    }
    const competitor = COMPETITORS.find(c => c.id === competitorId);
    if (!competitor) return { ok: false, reason: 'unknown_competitor' };

    const { SUPPORT_ROLES } = require('../../data/supportRoles');
    const analysis = analyzeCompetitor(competitor, {
      hiredSupport: state.hiredSupport || {},
      supportRoles: SUPPORT_ROLES,
      totalDays: state.totalDays || 0,
      playerState: state,
    });
    if (analysis.locked) return { ok: false, reason: 'analysis_locked', missing: analysis.missing };

    const askingPrice = computeAskingPrice(analysis);
    set({
      pendingAcquisition: {
        competitorId,
        stage: 1,
        stageStartDay: state.totalDays || 0,
        daysInStage: 0,
        analysis,
        askingPrice,
        weaknessLeverage: analysis.weakness?.leverage || 0,
        negotiationOutcome: null,
        antitrustOutcome: null,
      },
    });
    // Pedagogical milestone — first M&A opportunity triggered.
    if (typeof get().triggerMilestone === 'function') {
      try { get().triggerMilestone('first_acquisition', '/strategie'); }
      catch (err) { console.warn('[milestone] first_acquisition trigger failed:', err); }
    }
    return { ok: true };
  },

  /**
   * Advance to the next acquisition stage. Validates failure rolls for stages
   * that have a failureChance(Fn).
   */
  advanceAcquisitionStage: (negotiationStrategy = null) => {
    const state = get();
    const pa = state.pendingAcquisition;
    if (!pa) return { ok: false, reason: 'no_pending_acquisition' };
    const currentStage = getStageById(pa.stage);
    if (!currentStage) return { ok: false, reason: 'unknown_stage' };

    // Stage 3 (negotiation) needs a strategy choice
    if (pa.stage === 3 && !negotiationStrategy) {
      return { ok: false, reason: 'negotiation_strategy_required' };
    }

    let nextPatch = {
      stage: pa.stage + 1,
      stageStartDay: state.totalDays || 0,
      daysInStage: 0,
    };

    // ----- Stage 2: LOI -----
    if (pa.stage === 2) {
      const fail = (currentStage.failureChanceFn || (() => 0))({
        reputation: state.reputation || 0,
        notoriety: state.notoriety || 0,
      });
      if (Math.random() < fail) {
        // LOI refused → start cooldown, drop the acquisition
        set({
          pendingAcquisition: null,
          loiRefusalCooldown: {
            ...state.loiRefusalCooldown,
            [pa.competitorId]: state.totalDays || 0,
          },
        });
        if (typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage({
            senderType: 'advisor',
            // 31/07/2026 — cohérence : DAF si recruté, sinon conseiller.
            senderName: getFinanceSender(get()).senderName,
            tone: 'formal',
            kind: 'warning',
            subject: 'LOI refusée',
            // 15/06/2026 — message adapté au score : avec une bonne
            // réputation, le refus n'est plus mis (à tort) sur son compte.
            body: currentStage.failureMessageFn
              ? currentStage.failureMessageFn({ reputation: state.reputation || 0, notoriety: state.notoriety || 0 })
              : currentStage.failureMessage,
            ttlDays: 30,
          });
        }
        return { ok: false, reason: 'loi_refused' };
      }
    }

    // ----- Stage 3: negotiation -----
    if (pa.stage === 3) {
      const negotiation = evaluateNegotiation(pa.askingPrice, negotiationStrategy, {
        reputation: state.reputation || 50,
        weaknessLeverage: pa.weaknessLeverage,
      });
      nextPatch.negotiationOutcome = negotiation;
    }

    // ----- Stage 4: antitrust -----
    if (pa.stage === 4) {
      // Combined market share = our turnover + competitor's, over a notional regional total.
      const ourMonthly = state.monthlyRevenue || 0;
      const compMonthly = pa.analysis?.ca || 0;
      const combinedMarketShare = ((ourMonthly + compMonthly) / 80000) * 100; // 80k = marché local ~théorique
      const fail = (currentStage.failureChanceFn || (() => 0))({ combinedMarketShare });
      if (Math.random() < fail) {
        // Antitrust refused → charge the instruction cost, drop the deal
        const cost = currentStage.failureCost || 0;
        set({
          treasury: (state.treasury || 0) - cost,
          pendingAcquisition: null,
        });
        if (typeof get().pushCustomMessage === 'function') {
          get().pushCustomMessage({
            senderType: 'urssaf',
            senderName: 'Autorité de la Concurrence',
            tone: 'urgent',
            kind: 'warning',
            subject: 'Concentration refusée',
            body: `${currentStage.failureMessage} (Frais d'instruction : ${cost.toLocaleString('fr-FR')} €)`,
            ttlDays: 30,
          });
        }
        return { ok: false, reason: 'antitrust_refused', cost };
      }
      nextPatch.antitrustOutcome = { isAccepted: true };
    }

    set({
      pendingAcquisition: { ...pa, ...nextPatch },
    });
    return { ok: true, newStage: pa.stage + 1 };
  },

  /**
   * Cancel the acquisition (any stage). No refund of due diligence costs (none for now).
   */
  cancelAcquisition: () => set({ pendingAcquisition: null }),

  /**
   * Stage 5 — final closing. Transfers assets and creates the new depot.
   *
   * 05/02/2026 (Q1 retour user prod) — `managerId` obligatoire : le rachat
   * ne peut être finalisé sans qu'un responsable de site soit affecté à
   * l'Ex-dépôt.
   */
  completeAcquisition: (managerId = null) => {
    const state = get();
    const pa = state.pendingAcquisition;
    if (!pa || pa.stage !== 5) return { ok: false, reason: 'not_at_closing' };
    const competitor = COMPETITORS.find(c => c.id === pa.competitorId);
    if (!competitor) return { ok: false, reason: 'unknown_competitor' };

    // 05/02/2026 — Vérif manager obligatoire pour le rachat (cf. ouverture).
    if (!managerId) return { ok: false, reason: 'manager_required' };
    const hiredMgrs = state.hiredDepotManagers || {};
    if (hiredMgrs[managerId]) return { ok: false, reason: 'manager_already_hired' };
    const { getDepotManagerById: _getMgr } = require('../../data/depotManagers');
    const mgr = _getMgr(managerId);
    if (!mgr) return { ok: false, reason: 'unknown_manager' };

    // 29/05/2026 — defensive numeric guards: previous version trusted
    // negotiationOutcome.offerPrice + askingPrice to be numbers, but a
    // skipped negotiation stage left them undefined, propagating NaN to
    // treasury (visible as "Solde courant NaN" then clamped to 0 by
    // downstream sliders). We coerce both to safe numbers and bail out
    // explicitly if neither is usable.
    const rawOffer = pa.negotiationOutcome?.offerPrice;
    const rawAsking = pa.askingPrice;
    const safeFinalPrice = Number.isFinite(rawOffer)
      ? rawOffer
      : (Number.isFinite(rawAsking) ? rawAsking : null);
    if (safeFinalPrice === null) {
      console.warn('[completeAcquisition] missing offerPrice/askingPrice', pa);
      return { ok: false, reason: 'invalid_price' };
    }
    const finalPrice = safeFinalPrice;
    if ((state.treasury || 0) < finalPrice) {
      return { ok: false, reason: 'insufficient_funds', missing: finalPrice - (state.treasury || 0) };
    }

    // Deterministic PRNG so generated drivers/vehicles are stable.
    const rng = seededRng(`acq-${pa.competitorId}-${state.totalDays}`);

    // Generate the acquired roster
    // 02/06/2026 — Build the exclude-set so we never spawn an acquired
    // driver with the same `dataId` as someone already in the player's
    // roster (active OR fired+archived). Avoids duplicates reported by
    // the user after rachat de concurrent.
    const excludeDataIds = new Set();
    (state.drivers || []).forEach(d => { if (d.dataId) excludeDataIds.add(d.dataId); });
    Object.keys(state.firedDriversArchive || {}).forEach(id => excludeDataIds.add(id));
    // 22/07/2026 — retour user (« Hélène Vasseur ») : exclure aussi les
    // visages actuellement sur le marché RH ou chez un concurrent, sinon le
    // même nom/portrait apparaît en double (repris via rachat + vivier).
    ((state.driverPool || {}).availableIds || []).forEach(id => excludeDataIds.add(id));
    Object.keys((state.driverPool || {}).takenBy || {}).forEach(id => excludeDataIds.add(id));
    const newDrivers = generateCompetitorDrivers(competitor, pa.analysis, rng, excludeDataIds);
    // 02/06/2026 — Tier escalation : pass yearsPlayed so late-game
    // acquisitions inherit tier 4-5 vehicles (proportional to player
    // progression).
    const yearsPlayed = Math.max(0, Math.floor((state.totalDays || 0) / 360));
    const newVehicles = generateCompetitorVehicles(competitor, pa.analysis, rng, { yearsPlayed });
    const depotIdForVehicles = `depot-${competitor.id}`;
    newVehicles.forEach(v => { v.depotId = depotIdForVehicles; });

    // Driver retention pass
    const retention = simulateDriverRetention(newDrivers, state.reputation || 50, rng);
    retention.stayed.forEach(d => {
      d.depotId = depotIdForVehicles;
      // 13/06/2026 — Prorata congés selon le mois du closing (sinon les
      // chauffeurs rachetés arrivaient avec 25 j et la pression immédiate).
      d.holidayDaysRemaining = proRatedHolidayAllowance(state.month);
      d.hiredAt = state.totalDays;
    });

    // 13/06/2026 — Transfert des contrats en cours (croissance externe
    // réelle). Assigne 1-3 contrats réguliers aux paires véhicule+chauffeur
    // reprises. MUTATES newVehicles / retention.stayed (status → active).
    const inheritedContracts = generateInheritedContracts(
      competitor, pa.analysis, newVehicles, retention.stayed, rng, state.totalDays || 0,
    );

    // Build the new depot
    // 13/07/2026 — Progression unifiée : le concurrent a déjà développé son
    // site → le dépôt racheté DÉMARRE avec les investissements équivalents
    // (proportionnels à sa flotte) et poursuit ensuite le MÊME arbre que le
    // QG. `seedInvestmentCount` neutralise ces jalons hérités dans le calcul
    // du loyer (la taille du site au closing est déjà payée dans le prix).
    const seededInvestments = seedDepotInvestmentsForFleet(newVehicles.length);
    const newDepot = {
      id: depotIdForVehicles,
      name: `Ex-${competitor.name}`,
      brand: competitor.id,
      openedDay: state.totalDays || 0,
      capacityVehicles: Math.max(DEFAULT_DEPOT_CAPACITY, newVehicles.length),
      isHeadquarters: false,
      acquiredFrom: competitor.id,
      // 28/05/2026 v2 : flag splits the ownedDepots list into opened vs
      // acquired buckets. Read by `splitDepots` in depotEconomics.js.
      acquired: true,
      // 13/06/2026 — Chantier Dépôts : racheter un concurrent = couvrir SA
      // zone géographique (croissance externe par couverture).
      cityId: COMPETITOR_HOME_CITIES[competitor.id] || 'dijon',
      managerId, // 05/02/2026 — responsable assigné directement au rachat
      investments: seededInvestments,
      seedInvestmentCount: Object.keys(seededInvestments).length,
    };

    const integrationCost = Number.isFinite(MA_INTEGRATION_ONE_SHOT_COST) ? MA_INTEGRATION_ONE_SHOT_COST : 0;
    // 05/02/2026 — Prime de signature du manager incluse dans l'all-in.
    const managerSigningBonus = mgr.monthlyCost;
    const nextTreasury = (state.treasury || 0) - finalPrice - integrationCost - managerSigningBonus;

    // Summary for the closing modal (avec ligne dédiée manager signing bonus,
    // 05/02/2026 retour code review iteration_69).
    const summary = {
      competitorId: competitor.id,
      competitorName: competitor.name,
      finalPrice,
      integrationCost: MA_INTEGRATION_ONE_SHOT_COST,
      managerId, // 05/02/2026 — pour l'affichage du nom dans l'UI
      managerName: mgr.name,
      managerSigningBonus,
      allInCost: finalPrice + integrationCost + managerSigningBonus,
      driversInherited: retention.stayed.length,
      driversLeft: retention.left.length,
      vehiclesInherited: newVehicles.length,
      contractsInherited: inheritedContracts.length,
      contractsMonthlyValue: inheritedContracts.reduce((s, c) => s + (c.dailyRevenue || 0) * 22, 0),
      depotId: newDepot.id,
      depotName: newDepot.name,
      // 13/07/2026 — phase de développement héritée (affichée au closing).
      depotPhase: depotPhaseFromInvestments(seededInvestments),
      day: state.totalDays || 0,
    };
    set({
      // 28/05/2026 — M&A pricing now includes an 8 000 € one-shot
      // integration cost (IT harmonization + fleet uniformization).
      // Surfaces in the summary so the player understands the all-in
      // cost. The recurring monthly cost of the inherited depot is
      // computed live by getDepotMonthlyCost (3 500 € slot #2 or
      // 4 500 € slot #3).
      // 29/05/2026 — final NaN guard: if any addend went sideways, fall
      // back to the previous treasury rather than poisoning the slider.
      treasury: Number.isFinite(nextTreasury) ? nextTreasury : (state.treasury || 0),
      drivers: [...(state.drivers || []), ...retention.stayed],
      vehicles: [...(state.vehicles || []), ...newVehicles],
      activeContracts: [...(state.activeContracts || []), ...inheritedContracts],
      ownedDepots: [...(state.ownedDepots || []), newDepot],
      // 13/07/2026 — Progression unifiée : le dépôt racheté hérite des
      // investissements correspondant à son développement au closing.
      depotInvestments: {
        ...(state.depotInvestments || {}),
        [depotIdForVehicles]: { ...seededInvestments },
      },
      acquiredCompetitorIds: [...(state.acquiredCompetitorIds || []), competitor.id],
      pendingAcquisition: null,
      lastAcquisitionSummary: summary,
      // 05/02/2026 — Auto-embauche du manager choisi à l'étape M&A.
      hiredDepotManagers: {
        ...hiredMgrs,
        [managerId]: { hiredAt: state.totalDays || 0, assignedDepotId: depotIdForVehicles },
      },
      // Reputation boost: +5 for completing a successful M&A
      reputation: Math.min(100, (state.reputation || 0) + 5),
      notoriety: Math.min(100, (state.notoriety || 0) + 8),
    });

    if (typeof get().pushCustomMessage === 'function') {
      get().pushCustomMessage({
        senderType: 'advisor',
        senderName: 'DAF',
        tone: 'friendly',
        kind: 'info',
        subject: `Acquisition de ${competitor.name} finalisée`,
        body: `Closing signé. Vous prenez possession du dépôt « ${newDepot.name} », de ${newVehicles.length} véhicule${newVehicles.length > 1 ? 's' : ''} et de ${retention.stayed.length} chauffeur${retention.stayed.length > 1 ? 's' : ''}. ${inheritedContracts.length > 0 ? `${inheritedContracts.length} contrat${inheritedContracts.length > 1 ? 's' : ''} client en cours ${inheritedContracts.length > 1 ? 'sont repris' : 'est repris'} avec leurs équipages (≈ ${Math.round(inheritedContracts.reduce((s, c) => s + (c.dailyRevenue || 0) * 22, 0)).toLocaleString('fr-FR')} €/mois). ` : ''}${retention.left.length > 0 ? `${retention.left.length} chauffeur${retention.left.length > 1 ? 's ont' : ' a'} préféré ne pas suivre le rachat.` : 'Tous les chauffeurs vous ont suivi — beau travail.'}`,
        ttlDays: 60,
      });
    }

    // 17/06/2026 (Lot 6 Phase 2) — Effet "clan" : M&A est perçue comme un
    // acte prédateur par les autres concurrents → leur relation avec le
    // joueur se dégrade (-10 chacun). Le concurrent racheté est exclu
    // (relation absorbée). Branché sur acquisitionSlice puisque c'est
    // le seul lieu d'entrée d'un closing M&A.
    if (typeof get().bulkAdjustCompetitorRelations === 'function') {
      get().bulkAdjustCompetitorRelations(
        RELATION_DELTAS.M_AND_A_CLAN_EFFECT,
        competitor.id,
        `m_and_a_${competitor.id}`,
      );
    }

    // 31/05/2026 — Nathalie reactive follow-up (lands next day if hired).
    try {
      const fu = require('../helpers/nathalieEventFollowup').buildAcquisitionFollowup(
        get(),
        { ...summary, competitorName: competitor.name },
      );
      if (fu && typeof get().enqueueDeferredMessage === 'function') {
        get().enqueueDeferredMessage(
          (state.totalDays || 0) + 1,
          fu,
          `nathalie-ma-${competitor.id}`,
        );
      }
    } catch (err) { console.warn('[acquisitionSlice] M&A follow-up failed:', err); }

    // 08/02/2026 — Refonte phases (Q2). Rachat de concurrent →
    // promotion automatique en P3 (national) + vidéo Sora 2 dédiée
    // (competitor-buyout) + article de presse.
    tryPromotePhase(get, set, 3, `Rachat du concurrent ${competitor.name}`);
    if (typeof get().triggerMilestoneVideo === 'function') {
      try {
        get().triggerMilestoneVideo({
          kind: 'competitor-buyout',
          reason: `Rachat de ${competitor.name} finalisé`,
        });
      } catch (err) { console.warn('[acquisitionSlice] buyout video trigger failed:', err); }
    }
    return { ok: true, summary };
  },

  // ============== MULTI-DEPOT ==============
  /**
   * Open a brand-new independent depot.
   * Cost: NEW_DEPOT_OPEN_COST. Requires available depot slot.
   *
   * 05/02/2026 (Q1 retour user prod) — `managerId` obligatoire : un dépôt
   * ne peut pas être créé sans qu'un responsable de site soit choisi
   * (parmi les profils non-encore-recrutés). L'embauche est facturée en
   * sus du coût d'implantation (prime de signature = 1 mois de salaire).
   */
  openNewDepot: (depotName = 'Nouveau dépôt', cityId = null, managerId = null) => {
    const state = get();
    const depots = state.ownedDepots || [];

    // 05/02/2026 (retour code review iteration_69) — Vérifs manager
    // déplacées EN PREMIER. La règle métier la plus saillante doit
    // remonter en tête : sans responsable, aucun dépôt ne peut s'ouvrir,
    // peu importe la ville/phase/quota. L'UI affiche le picker dès
    // l'ouverture de la modale et ne valide que si tout est rempli.
    if (!managerId) {
      return { ok: false, reason: 'manager_required' };
    }
    const hiredMgrs = state.hiredDepotManagers || {};
    if (hiredMgrs[managerId]) {
      return { ok: false, reason: 'manager_already_hired' };
    }
    const { getDepotManagerById: _getMgr } = require('../../data/depotManagers');
    const mgr = _getMgr(managerId);
    if (!mgr) return { ok: false, reason: 'unknown_manager' };

    // 13/06/2026 — Chantier Dépôts : ouverture ancrée dans une ville.
    //   - 3 dépôts ouverts max en France (QG inclus),
    //   - +2 slots INTERNATIONAUX (villes UE, phase 4 requise),
    //   - une seule implantation par ville,
    //   - subvention régionale à l'implantation : −30 % du coût d'ouverture.
    const city = cityId ? findCityById(cityId) : null;
    if (cityId && !city) return { ok: false, reason: 'unknown_city' };
    const isInternational = city ? city.country !== 'FR' : false;

    if (city && depots.some(d => getDepotCityId(d, state.company) === city.id)) {
      return { ok: false, reason: 'city_already_covered' };
    }
    if (city && city.phase > (state.phase || 1)) {
      // 10/07/2026 (fix user prod) — verrou circulaire : la P3→P4 se
      // déclenche en ouvrant un dépôt étranger… mais les villes étrangères
      // exigeaient déjà la phase 4. Dès la PHASE 3, les villes phase 4
      // deviennent ouvrables (l'ouverture promeut alors en phase 4).
      const phaseCap = (state.phase || 1) >= 3 ? 4 : (state.phase || 1);
      if (city.phase > phaseCap) {
        return { ok: false, reason: 'city_phase_locked', requiredPhase: city.phase };
      }
    }
    // 17/06/2026 — Refonte dépôts : limite TOTALE par phase (3 / 5 / 10).
    const maxTotal = getMaxDepotsForPhase(state.phase || 1);
    if (depots.length >= maxTotal) {
      return { ok: false, reason: 'max_depots_phase', maxTotal, phase: state.phase || 1 };
    }
    if (isInternational) {
      const intlCount = depots.filter(d => {
        const c = findCityById(getDepotCityId(d, state.company));
        return c && c.country !== 'FR';
      }).length;
      if (intlCount >= MAX_INTERNATIONAL_DEPOTS) {
        return { ok: false, reason: 'max_international_depots' };
      }
    } else {
      const { opened } = splitDepots(depots);
      const openedFr = opened.filter(d => {
        const c = findCityById(getDepotCityId(d, state.company));
        return !c || c.country === 'FR';
      });
      if (openedFr.length >= MAX_OPENED_DEPOTS) return { ok: false, reason: 'max_opened_depots_reached' };
    }

    // 08/02/2026 — Q2 Phase 3 user prod : suppression de la subvention
    // d'ouverture. Le user veut que la création d'un dépôt parte « à 0 »
    // et que toutes les évolutions soient repayées. Plus de remise.
    const subsidy = 0;
    const netCost = NEW_DEPOT_OPEN_COST;
    // Coût total = implantation + prime de signature manager (1 mois).
    const totalCost = netCost + mgr.monthlyCost;
    if ((state.treasury || 0) < totalCost) {
      return { ok: false, reason: 'insufficient_funds', cost: totalCost };
    }

    const id = `depot-indep-${Date.now()}`;
    const newDepot = {
      id,
      name: depotName.trim() || `Dépôt ${city?.name || depots.length + 1}`,
      brand: 'independant',
      openedDay: state.totalDays || 0,
      capacityVehicles: DEFAULT_DEPOT_CAPACITY,
      isHeadquarters: false,
      cityId: city ? city.id : null,
      managerId, // 05/02/2026 — responsable assigné directement à l'ouverture
      // 13/07/2026 — Progression unifiée : dépôt créé = démarre à zéro et
      // suit le même arbre d'investissements que le QG.
      investments: {},
    };
    set({
      treasury: (state.treasury || 0) - totalCost,
      ownedDepots: [...depots, newDepot],
      activeDepotId: id,
      // 08/02/2026 — Q2 Phase 3 user prod : nouveau dépôt = depotInvestments
      // vide (le joueur repaye chaque évolution).
      depotInvestments: {
        ...(state.depotInvestments || {}),
        [id]: {},
      },
      // Auto-embauche du manager choisi.
      hiredDepotManagers: {
        ...hiredMgrs,
        [managerId]: { hiredAt: state.totalDays || 0, assignedDepotId: id },
      },
      notifications: [...(state.notifications || []).slice(-30), {
        id: `depot-open-${id}`,
        type: 'depot_opened',
        title: `Dépôt ouvert${city ? ` à ${city.name}` : ''}`,
        message: `Coût d'implantation ${NEW_DEPOT_OPEN_COST.toLocaleString('fr-FR')} € − aide régionale ${subsidy.toLocaleString('fr-FR')} € = ${netCost.toLocaleString('fr-FR')} €. Responsable ${mgr.name} embauché (${mgr.monthlyCost.toLocaleString('fr-FR')} € prime de signature). Loyer réduit de 50 % pendant 6 mois (aide à l'implantation).`,
        day: state.totalDays,
        read: false,
      }],
    });

    // 31/05/2026 — Nathalie reactive follow-up (lands next day if hired).
    try {
      const fu = require('../helpers/nathalieEventFollowup').buildDepotOpenedFollowup(
        get(),
        newDepot,
      );
      if (fu && typeof get().enqueueDeferredMessage === 'function') {
        get().enqueueDeferredMessage(
          (state.totalDays || 0) + 1,
          fu,
          `nathalie-depot-${id}`,
        );
      }
    } catch (err) { console.warn('[acquisitionSlice] depot opened follow-up failed:', err); }

    // 08/02/2026 — Refonte phases (Q2). Ouverture d'un dépôt :
    //   - Dépôt à l'étranger → promotion P4 (international).
    //   - Dépôt FR (et ce n'est pas le QG, déjà créé via bootstrapDepots) →
    //     promotion P3 (national) si on n'y est pas déjà.
    //   - Dans tous les cas, vidéo Sora 2 « depot-open ».
    if (isInternational) {
      tryPromotePhase(get, set, 4, `Premier dépôt international à ${city ? city.name : ''}`);
    } else {
      tryPromotePhase(get, set, 3, `Nouveau dépôt FR à ${city ? city.name : ''}`);
    }
    if (typeof get().triggerMilestoneVideo === 'function') {
      try {
        get().triggerMilestoneVideo({
          kind: 'depot-open',
          reason: `Dépôt « ${newDepot.name} » inauguré${city ? ` à ${city.name}` : ''}`,
        });
      } catch (err) { console.warn('[acquisitionSlice] depot video trigger failed:', err); }
    }

    return { ok: true, depot: newDepot };
  },

  switchActiveDepot: (depotId) => {
    const state = get();
    if (!(state.ownedDepots || []).some(d => d.id === depotId)) return false;
    set({ activeDepotId: depotId });
    return true;
  },

  /**
   * 13/07/2026 — Progression unifiée : l'ancienne action `upgradeDepot`
   * (catalogue DEPOT_UPGRADES) est supprimée. Les dépôts secondaires
   * évoluent via financeSlice.activateDepotInvestment (même arbre que le
   * QG). Les anciennes évolutions des sauvegardes existantes restent
   * effectives via LEGACY_UPGRADE_TO_INVESTMENT (data/depotPhases.js).
   */

  // Expose constants for the UI to import
  _acquisitionConstants: {
    MAX_DEPOTS,
    NEW_DEPOT_OPEN_COST,
    DEFAULT_DEPOT_CAPACITY,
  },
});

export const ACQUISITION_CONSTANTS = {
  MAX_DEPOTS,
  NEW_DEPOT_OPEN_COST,
  DEFAULT_DEPOT_CAPACITY,
};
